import { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

export const PWA_OVERRIDE_KEY = "DfsWeb.pwaOverride";
export const PWA_QUERY_PARAM = "pwa";
const STANDALONE_QUERY = "(display-mode: standalone)";

const Ctx = createContext(null);

export const detectStandalone = () =>
  Boolean(window.matchMedia?.(STANDALONE_QUERY)?.matches) ||
  window.navigator.standalone === true ||
  document.referrer.startsWith("android-app://");

// ?pwa=0 clears the override; it does not force web mode.
export const resolveOverride = (search = window.location.search) => {
  try {
    const param = new URLSearchParams(search).get(PWA_QUERY_PARAM);
    if (param === "1") sessionStorage.setItem(PWA_OVERRIDE_KEY, "1");
    else if (param === "0") sessionStorage.removeItem(PWA_OVERRIDE_KEY);
    return sessionStorage.getItem(PWA_OVERRIDE_KEY) === "1";
  } catch {
    return false;
  }
};

export const readDisplayMode = (search) => {
  const standalone = resolveOverride(search) || detectStandalone();
  return { standalone, mode: standalone ? "standalone" : "browser" };
};

export function DisplayModeProvider({ children }) {
  const { search } = useLocation();
  const [standalone, setStandalone] = useState(() => readDisplayMode(search).standalone);

  useEffect(() => {
    const refresh = () => setStandalone(resolveOverride(search) || detectStandalone());
    refresh();
    const mql = window.matchMedia?.(STANDALONE_QUERY);
    if (!mql) return undefined;
    if (mql.addEventListener) mql.addEventListener("change", refresh);
    else mql.addListener?.(refresh);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", refresh);
      else mql.removeListener?.(refresh);
    };
  }, [search]);

  useEffect(() => {
    document.documentElement.dataset.displayMode = standalone ? "standalone" : "browser";
  }, [standalone]);

  const value = useMemo(
    () => ({ standalone, mode: standalone ? "standalone" : "browser" }),
    [standalone]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

DisplayModeProvider.propTypes = { children: PropTypes.node };

export const useDisplayMode = () => useContext(Ctx) ?? readDisplayMode();
